"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, FileText, Gauge, Layers, Settings, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const keyCapabilities = [{
  id: "cap-1",
  label: "Compact Single-Point FT-NIR",
  detail: "Direct optical measurement at target process locations"
}, {
  id: "cap-2",
  label: "Solids & Semi-Solids Analysis",
  detail: "Real-time non-destructive spectroscopic measurement"
}, {
  id: "cap-3",
  label: "Flexible Interface Mounting",
  detail: "Adaptable to conveyors, hoppers, and process interfaces"
}, {
  id: "cap-4",
  label: "Method & Transfer Support",
  detail: "Method development and production transfer assistance"
}];
const quickMetrics = [{
  id: "m-1",
  value: "",
  label: "Performance depends on the instrument, application, sample matrix and validated method"
}, {
  id: "m-2",
  value: "Non-Destructive",
  label: "Measurement"
}, {
  id: "m-3",
  value: "IP65",
  label: "Washdown Rating"
}];
export default function BrukerBeam() {
  const shouldReduceMotion = useReducedMotion();
  const motionInitial = shouldReduceMotion ? false : {
    opacity: 0,
    y: 16
  };
  const motionAnimate = {
    opacity: 1,
    y: 0
  };
  const motionTransition = {
    duration: 0.5,
    ease: "easeOut"
  };
  return <section id="herobrukerbeam" data-nav="light" className="relative w-full overflow-hidden bg-background py-16 md:py-24 lg:py-28 text-foreground">
      {/* Decorative ambient background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 -right-20 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute top-1/2 -left-24 h-80 w-80 rounded-full bg-secondary blur-3xl" />
      </div>

      <div className="page-container">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          {/* Left Column: Product Positioning & Details */}
          <div className="flex flex-col items-start lg:col-span-7">
            <motion.div initial={motionInitial} animate={motionAnimate} transition={motionTransition} className="flex flex-wrap items-center gap-2.5 pb-4">
              <Badge variant="secondary" className="px-3 py-1 font-medium text-secondary-foreground">
                <Sparkles className="size-3.5 text-primary" />
                Bruker PAT Series
              </Badge>
              <Badge variant="outline" className="border-border text-muted-foreground">
                Process Analytical Technology
              </Badge>
            </motion.div>

            <motion.h1 initial={motionInitial} animate={motionAnimate} transition={{
            ...motionTransition,
            delay: 0.08
          }} className="font-default text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl text-foreground">
              Bruker BEAM
              <span className="block pt-1 text-primary">
                FT-NIR Process Analyzer
              </span>
            </motion.h1>

            <motion.p initial={motionInitial} animate={motionAnimate} transition={{
            ...motionTransition,
            delay: 0.16
          }} className="mt-5 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-2xl">
              A compact, single-point FT-NIR process analyzer for real-time, non-destructive 
              measurement of solids and semi-solids. Designed with flexible mounting at conveyors, 
              hoppers, and process interfaces, fully supported through method development and 
              production transfer by Antech Solutions across Ireland.
            </motion.p>

            {/* Feature Checkpoints */}
            <motion.div initial={motionInitial} animate={motionAnimate} transition={{
            ...motionTransition,
            delay: 0.22
          }} className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3.5 w-full max-w-2xl">
              {keyCapabilities.map((cap, i) => <div key={cap.id} data-index={i} className="flex items-start gap-2.5 rounded-lg border border-border bg-card/60 p-3 shadow-2xs backdrop-blur-xs">
                  <CheckCircle2 className="size-5 shrink-0 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {cap.label}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {cap.detail}
                    </p>
                  </div>
                </div>)}
            </motion.div>

            {/* CTAs */}
            <motion.div initial={motionInitial} animate={motionAnimate} transition={{
            ...motionTransition,
            delay: 0.3
          }} className="mt-8 flex flex-wrap items-center gap-3.5">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/BookADemoPage">
                  Book a Demo
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg">
                <Link to="/DiscussYourApplicationPage">
                  Discuss Application
                </Link>
              </Button>

              <Button asChild variant="ghost" size="lg" className="text-muted-foreground hover:text-foreground">
                <Link to="/ProcessAnalyticalTechnologyPage">
                  View PAT Range
                </Link>
              </Button>
            </motion.div>

            {/* Support / Lifecycle strip */}
            <motion.div initial={motionInitial} animate={motionAnimate} transition={{
            ...motionTransition,
            delay: 0.36
          }} className="mt-8 flex flex-wrap items-center gap-4 text-xs text-muted-foreground border-t border-border pt-4 w-full">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="size-4 text-primary" />
                <span>IQ/OQ Qualification Support</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Cpu className="size-4 text-primary" />
                <span>OPC UA & SCADA Ready</span>
              </div>
              <div className="flex items-center gap-1.5">
                <FileText className="size-4 text-primary" />
                <span>Method Transfer Support</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Settings className="size-4 text-primary" />
                <span>Feasibility, Chemometrics & Installation Support in Ireland</span>
              </div>
            </motion.div>
          </div>

          {/* Right Column: Visual Product Showcase Card */}
          <div className="lg:col-span-5">
            <motion.div initial={motionInitial} animate={motionAnimate} transition={{
            ...motionTransition,
            delay: 0.2
          }} className="relative">
              {/* Product Card Container */}
              <Card className="relative overflow-hidden border border-border shadow-lg bg-card">
                
                {/* Header status strip */}
                <div className="flex items-center justify-between border-b border-border bg-muted/40 px-5 py-3">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" />
                    </span>
                    <span className="text-xs font-semibold tracking-wide uppercase text-foreground">
                      In-Line NIR Analyzer
                    </span>
                  </div>
                  <Badge variant="outline" className="bg-background text-[11px] font-mono">
                    Bruker Optics PAT
                  </Badge>
                </div>

                {/* Primary Image Container */}
                <div className="relative aspect-4/3 w-full bg-secondary/30 flex items-center justify-center p-4">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_beam_analyzer_system.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_beam_analyzer_system.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_beam_analyzer_system.webp-wvc-sizes" alt="Bruker BEAM Process Analytical Technology near infrared spectrometer" loading="lazy" decoding="async" className="h-full w-full object-contain object-center transition-transform duration-300 hover:scale-102" />

                  {/* Floating Specs Pill */}
                  <div className="absolute bottom-3 left-3 rounded-md border border-border bg-card/90 px-3 py-1.5 shadow-sm backdrop-blur-xs">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                      <Activity className="size-3.5 text-primary" />
                      <span>Real-Time Non-Destructive Measurement</span>
                    </div>
                  </div>
                </div>

                <CardContent className="p-5">
                  <div className="space-y-4">
                    {/* Metrics Bar */}
                    <div className="grid grid-cols-3 gap-2 rounded-lg bg-secondary/60 p-3 text-center items-center">
                      {quickMetrics.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col justify-center">
                          {item.value ? <span className="font-mono text-base font-bold text-foreground">
                              {item.value}
                            </span> : null}
                          <span className="text-[11px] text-muted-foreground mt-0.5">
                            {item.label}
                          </span>
                        </div>)}
                    </div>

                    <Separator />

                    {/* Integrated Capabilities Summary */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-1.5 text-muted-foreground font-medium">
                          <Gauge className="size-3.5 text-primary" />
                          Application Domains
                        </span>
                        <span className="font-semibold text-foreground">
                          Conveyors, Hoppers, Process Interfaces
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-1.5 text-muted-foreground font-medium">
                          <Layers className="size-3.5 text-primary" />
                          Spectral Range
                        </span>
                        <span className="font-semibold text-foreground">
                          Near Infrared (NIR)
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Decorative background border glow */}
              <div aria-hidden="true" className="absolute -inset-1 -z-10 rounded-2xl bg-gradient-to-r from-primary/20 via-transparent to-primary/10 blur-sm" />
            </motion.div>
          </div>

        </div>
      </div>
    </section>;
}